<script>
import BaseSimpleTable from '@/components/_base-simple-table.vue'
import dateMixin from '@/mixins/dateMixin'
import VuetifyTextClass from '@/shared/constants/core/VuetifyTextClass'
import VuetifyColourClass from '@/shared/constants/core/VuetifyColourClass'
import { mapGetters } from 'vuex'
import DateFormatToken from '@/shared/constants/date/DateFormatToken'
import CandidateDropdown from './extend-booking-candidate-dropdown.vue'

export default {
  components: { BaseSimpleTable, CandidateDropdown },
  mixins: [dateMixin],
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    value: {
      type: Array,
      required: true,
    },
    candidateOptions: {
      type: Array,
      required: true,
    },
    confirmedBookings: {
      type: Array,
      required: true,
    },
    unavailableWarning: {
      type: String,
      default: '',
    },
    extraValidation: {
      type: Object,
      required: true,
    },
    isAnyBookingClosedInSchedule: {
      type: Boolean,
      default: false,
    },
    areTimesDifferentToSchedule: {
      type: Function,
      required: true,
    },
    isDateClosedInSchedule: {
      type: Function,
      required: true,
    },
    showChangeTimesButton: {
      type: Boolean,
      default: false,
    },
    changeTimesButtonText: {
      type: String,
      default: '',
    },
    scheduleClosureWarning: {
      type: String,
      default: '',
    },
  },
  data: () => ({
    ...VuetifyTextClass,
    text: VuetifyColourClass.text,
    primary: VuetifyColourClass.blue,
    grey600: VuetifyColourClass.grey600,
    warning400: VuetifyColourClass.warning400,
    warning100: VuetifyColourClass.warning100,
    blue200: VuetifyColourClass.blue200,
    blue400: VuetifyColourClass.blue400,
    HHmm: DateFormatToken.HHmm,
    headers: ['date', 'educator', 'start', 'end'],
  }),
  computed: {
    ...mapGetters({
      isLoading: 'bookings/isLoadingExtendBooking',
    }),
    summaryBookings: {
      get() {
        return this.value || []
      },
      set(newValue) {
        this.$emit('input', newValue)
      },
    },
    disableRemove() {
      return (
        !this.confirmedBookings?.length || this.confirmedBookings.length < 2
      )
    },
    showRequiredDaysWarning() {
      return this.disableRemove && this.summaryBookings?.length > 1
    },
    shouldShowWarning() {
      return Boolean(this.unavailableWarning?.trim()?.length)
    },
    centeredHeaders() {
      return [
        {
          value: 'name',
          title: 'educator',
        },
        {
          value: 'startTime',
          title: 'start',
        },
        {
          value: 'endTime',
          title: 'end',
        },
      ]
    },

    isInfoAlert() {
      return [
        this.scheduleClosureWarning,
        this.$t('booking.extend.outsideWorkScheduleTimesWarning'),
      ].includes(this.unavailableWarning)
    },
  },

  methods: {
    setCandidate(tableItemId, dropdownItem) {
      const { candidateId, name } = dropdownItem
      this.$emit('setCandidate', {
        id: tableItemId,
        candidateId,
        name,
      })
    },
    setTime(value, id, field) {
      this.$emit('setTime', { id, field, value })

      // Wait for DOM update, then validate both fields for this row
      this.$nextTick(() => {
        const startRef = this.$refs['startTime-' + id]
        const endRef = this.$refs['endTime-' + id]
        if (startRef && startRef.validate) startRef.validate()
        if (endRef && endRef.validate) endRef.validate()
      })
    },
    isTimeHighlighted(item, field) {
      if (item.removed || !this.isInfoAlert) return false

      return this.areTimesDifferentToSchedule(item)?.field === field
    },
    isClosedInSchedule(item) {
      if (item.removed) return false

      return this.isDateClosedInSchedule(item.dateMeta)
    },
  },
}
</script>

<template>
  <div class="mt-2">
    <div class="d-flex justify-space-between align-center mb-4">
      <p class="mb-0" :class="smallBody">{{
        $t('booking.extend.summarySubtitle')
      }}</p>
      <transition name="fade-up" mode="out-in">
        <BaseButton
          v-if="showChangeTimesButton"
          secondary
          class="font-weight-medium rounded-lg"
          @click="$emit('toggleScheduledTimes')"
        >
          <transition name="fade-cycle-down" mode="out-in">
            <span :key="changeTimesButtonText" class="px-2">{{
              changeTimesButtonText
            }}</span>
          </transition>
        </BaseButton>
      </transition>
    </div>

    <transition name="fade-up">
      <div
        v-if="shouldShowWarning"
        :class="[
          isInfoAlert ? blue200 : warning100,
          'd-flex align-center pa-4 rounded-lg transition-bg',
        ]"
      >
        <v-icon
          class="mr-4 transition-color"
          size="22"
          :color="isInfoAlert ? blue400 : warning400"
          >mdi-information</v-icon
        >
        <transition name="fade-cycle-down" mode="out-in">
          <p
            :key="unavailableWarning"
            :class="[
              smallBody,
              isInfoAlert ? text.blue600 : text.warning500,
              'mb-0',
              'transition-color',
            ]"
          >
            {{ unavailableWarning || $t('booking.extend.genericWarning') }}
          </p>
        </transition>
      </div>
    </transition>

    <BaseSimpleTable
      v-model="summaryBookings"
      :headers="headers"
      :centered-headers="centeredHeaders"
      :excluded-keys="['dateMeta']"
      :allowed-keys="['date', 'name', 'startTime', 'endTime']"
      fixed-header
      removable
      :disable-remove="disableRemove"
      height="350px"
      class="mt-4"
    >
      <template v-slot:date-header="{ header }">
        <span>
          <v-icon
            class="mr-1 icon-offset transition-opacity"
            :class="{ 'opacity-0': !isAnyBookingClosedInSchedule }"
            size="18"
            :color="blue400"
            >mdi-information-outline</v-icon
          >
          {{ header }}
        </span>
      </template>
      <template v-slot:date-cell="{ item }">
        <span>
          <v-icon
            class="mr-1 icon-offset transition-opacity"
            :class="{ 'opacity-0': !isClosedInSchedule(item) }"
            size="18"
            :color="blue400"
            >mdi-information-outline</v-icon
          >
          {{ item.date }}
        </span>
      </template>
      <template v-slot:name-cell="{ item }">
        <CandidateDropdown
          :table-item="item"
          :candidate-options="candidateOptions"
          @setCandidate="(payload) => setCandidate(item.id, payload)"
        />
      </template>
      <template v-slot:startTime-cell="{ item }">
        <v-text-field
          :ref="'startTime-' + item.id"
          type="time"
          :value="item.startTime"
          dense
          outlined
          hide-details
          :background-color="
            isTimeHighlighted(item, 'startTime') ? blue200 : ''
          "
          :disabled="item.removed"
          :color="primary"
          :rules="[
            (v) =>
              isAfter(
                v,
                extraValidation.earliestStartTime24hr,
                durationUnits.MINUTE
              ) ||
              $t('booking.extend.startTimeBeforeMinWarning', {
                time: extraValidation.earliestStartTime24hr,
              }),
            (v) =>
              isBefore(v, item.endTime, durationUnits.MINUTE) ||
              $t('booking.extend.invalidStartTimeWarning'),
            (v) =>
              diff(item.endTime, v, durationUnits.MINUTE) >=
                extraValidation.minimumDurationInMins ||
              $t('booking.extend.invalidTimesMinDurationWarning', [
                `${extraValidation.minimumDurationInMins} minutes`,
              ]),
          ]"
          class="mx-auto"
          @input="(v) => setTime(v, item.id, 'startTime')"
        />
      </template>
      <template v-slot:endTime-cell="{ item }">
        <v-text-field
          :ref="'endTime-' + item.id"
          type="time"
          :value="item.endTime"
          dense
          outlined
          hide-details
          :background-color="isTimeHighlighted(item, 'endTime') ? blue200 : ''"
          :disabled="item.removed"
          :color="primary"
          :rules="[
            (v) =>
              isAfter(v, item.startTime, durationUnits.MINUTE) ||
              $t('booking.extend.invalidEndTimeWarning'),
            (v) =>
              diff(v, item.startTime, durationUnits.MINUTE) >
                extraValidation.minimumDurationInMins ||
              $t('booking.extend.invalidTimesMinDurationWarning', [
                `${extraValidation.minimumDurationInMins} minutes`,
              ]),
          ]"
          class="mx-auto"
          @input="(v) => setTime(v, item.id, 'endTime')"
        />
      </template>
    </BaseSimpleTable>
    <div v-if="showRequiredDaysWarning" class="d-flex mt-4">
      <v-icon class="mr-2" size="22" :color="grey600"
        >mdi-information-outline</v-icon
      >
      <p class="mb-0" :class="text.grey600">{{
        $t('booking.extend.oneDayRequired')
      }}</p>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import '@/styles/_vue-transitions.scss';

.icon-offset {
  margin-bottom: 2px !important;
}
</style>
